import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useImportSrf } from '@/entities/consignments';
import { RouteNames } from '@/shared/config/routes';
import { getApiErrorMessage, useProjectSiteNavigation } from '@/shared/lib';

interface UseSrfUploadParams {
  /** Called after a successful import, before navigation (e.g. to close the modal). */
  onSuccess?: () => void;
}

/**
 * Owns the SRF upload UI flow: file selection (`.xlsx` / size validation lives
 * in `FileUpload`), kicking off the import (via the `useImportSrf` entity
 * helper, which encapsulates the mutation + error handling), surfacing `400`
 * errors inline, and navigating to the Edit Consignment page on success.
 */
export function useSrfUpload({ onSuccess }: UseSrfUploadParams = {}) {
  const { t } = useTranslation();
  const { navigateWithContext, projectId, siteId } = useProjectSiteNavigation();

  const [file, setFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { importSrf, isLoading } = useImportSrf({
    onError: (error) => {
      setErrorMessage(getApiErrorMessage(error, t('srf.upload.error', 'Failed to upload SRF file. Please try again.')));
    }
  });

  const handleFileSelect = useCallback((files: FileList) => {
    if (files.length === 0) return;
    setErrorMessage(null);
    setFile(files[0]);
  }, []);

  const handleFileError = useCallback(() => {
    setFile(null);
    setErrorMessage(t('srf.upload.invalidFile', 'Only .xlsx SRF files up to 20 MB are accepted'));
  }, [t]);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
  }, []);

  const resetForm = useCallback(() => {
    setFile(null);
    setErrorMessage(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!file) return;

    setErrorMessage(null);
    // Errors are surfaced through the entity helper's `onError`; swallow the
    // re-throw here so the success path stays linear.
    const consignment = await importSrf({ projectId, siteId, file }).catch(() => undefined);
    if (!consignment) return;

    toast.success(t('srf.upload.success', 'SRF uploaded — consignment created'));
    onSuccess?.();
    resetForm();
    navigateWithContext(RouteNames.CONSIGNMENT_EDIT, { consignmentId: consignment.id });
  }, [file, importSrf, projectId, siteId, t, onSuccess, resetForm, navigateWithContext]);

  return {
    file,
    errorMessage,
    isLoading,
    handleFileSelect,
    handleFileError,
    handleRemoveFile,
    handleSubmit,
    resetForm
  };
}
